import { useState } from "react";
import { formatDate } from "date-fns";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EventInteractionArgs } from "react-big-calendar/lib/addons/dragAndDrop";
import { usePostponeExpeditionBo } from "@/api/expedition.api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { PickupTimeCotationEnum } from "@/constants/enums/pickupTimeCotation.enum";
import { CalendarEventType } from "@/types/bo/calendarType";
import { SelectV2 } from "@/components/ui/customs/select-v2";
import { usePickupSlotOptions } from "@/hooks/useEnumOptions";

type CollectionEventDropModalProps = {
  closeModal: () => void;
  dropEvent: EventInteractionArgs<CalendarEventType>;
};

export const CollectionEventDropModal = ({
  closeModal,
  dropEvent,
}: CollectionEventDropModalProps) => {
  const { t } = useTranslation(["boLogistics", "common"]);
  const pickupSlotOptions = usePickupSlotOptions();
  const [pickupSlot, setPickupSlot] = useState<PickupTimeCotationEnum>(
    PickupTimeCotationEnum.MORNING,
  );

  const { mutateAsync: postponeExpeditionBo, isPending } = usePostponeExpeditionBo();

  const handlePostpone = async () => {
    await postponeExpeditionBo({
      id: dropEvent.event.expeditionId,
      payload: {
        pickupDate: new Date(dropEvent.start).toISOString(),
        pickupSlot,
      },
    });
    closeModal();
  };

  return (
    <Modal
      open
      onClose={closeModal}
      title={t("eventDropModal.title")}
      footer={
        <div className="flex w-full flex-row gap-2">
          <Button variant="outline" onClick={closeModal} className="flex-1" disabled={isPending}>
            {t("common:actions.cancel")}
          </Button>
          <Button onClick={handlePostpone} className="flex-1" disabled={isPending}>
            {isPending ? <Loader2 className="animate-spin" /> : t("eventDropModal.submit")}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <span className="text-center text-sm font-medium text-gray-900">
          {t("eventDropModal.description", {
            fromDate: formatDate(dropEvent.event.start, "dd/MM/yyyy"),
            toDate: formatDate(dropEvent.start, "dd/MM/yyyy"),
          })}
        </span>
        <SelectV2
          value={pickupSlot}
          onChange={(value) => setPickupSlot(value as PickupTimeCotationEnum)}
          options={pickupSlotOptions}
          placeholder={t("eventDropModal.slotPlaceholder")}
          contentClassName="w-full"
        />
      </div>
    </Modal>
  );
};
